import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import "./AddEventFormPage.css";

const API_BASE_URL = process.env.REACT_APP_BACKEND_API_URL;

export default function AddEventFormPage() {
  const navigate = useNavigate();

  // ===============================
  // Current user
  // ===============================
  const [currentUser, setCurrentUser] = useState({ name: "" });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user) {
      setCurrentUser({ name: user.name || user.fullname || "Admin" });
    }
  }, []);

  // ===============================
  // Form state
  // ===============================
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    venue: "",
    date: "",
    postedBy: "",
    image: null,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ✅ Set postedBy once currentUser loads
  useEffect(() => {
    if (currentUser.name) {
      setFormData((prev) => ({
        ...prev,
        postedBy: currentUser.name,
      }));
    }
  }, [currentUser]);

  // ===============================
  // Handlers
  // ===============================
  const handleChange = (e) => {
    const { name, value, files } = e.target;

    if (name === "image") {
      setFormData((prev) => ({ ...prev, image: files[0] }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (
      !formData.title ||
      !formData.description ||
      !formData.venue ||
      !formData.date
    ) {
      setError("Please fill all required fields.");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = new FormData();
      data.append("title", formData.title);
      data.append("description", formData.description);
      data.append("venue", formData.venue);
      data.append("date", formData.date);
      data.append("postedBy", formData.postedBy);

      if (formData.image) {
        data.append("image", formData.image);
      }

      const res = await axios.post(`${API_BASE_URL}/events`, data, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      alert(res.data.message || "Event created successfully!");
      navigate("/admin-panel/events-page"); // adjust route as needed
    } catch (err) {
      console.error("Failed to create event:", err.response || err.message);
      setError(
        err.response?.data?.message || "Failed to create event. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-event-page">
      <motion.div
        className="add-event-card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <button
          type="button"
          className="back-btn"
          onClick={() => navigate(-1)}
        >
          ← Back
        </button>

        <h2>Create New Event</h2>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              placeholder="Enter event title"
              value={formData.title}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Description</label>
            <textarea
              name="description"
              placeholder="Enter event description"
              value={formData.description}
              onChange={handleChange}
              rows={4}
              required
            />
          </div>

          <div className="form-group">
            <label>Venue</label>
            <input
              type="text"
              name="venue"
              placeholder="Enter event venue"
              value={formData.venue}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Date</label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Event Image (Optional)</label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleChange}
            />
          </div>

          <div className="form-group">
            <label>Posted By</label>
            <input
              type="text"
              name="postedBy"
              value={formData.postedBy}
              disabled
            />
          </div>

          {error && <div className="error-msg">{error}</div>}

          <motion.button
            type="submit"
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Event"}
          </motion.button>
        </form>
      </motion.div>
    </div>
  );
}